import { Deletable, Identificable } from 'shared/types/commonView';
import { findById } from 'shared/utils/id/findById';
import { isUploadStatusSuccess } from 'shared/types/uploadStatus';
import { isSupportedAsMap } from './availableExtensionsAsMap';
import { findCurrentVersion, VersionedDocumentation } from './currentVersion';
import type { VersionModel } from './documentationModel';

/** What the map rules read of a version. */
export type MapVersion = Identificable &
  Deletable &
  Pick<VersionModel, 'uploadStatus' | 'extension' | 'imageConvertStatus'>;

type MapDocumentation<TVersion extends MapVersion> = VersionedDocumentation<TVersion>;

/**
 * A map to draw (PT-7117): a documentation in whichever version is current,
 * as a level points at its map, or one version of it, the one an issue was
 * located on.
 */
export interface MapReference {
  documentationId: string;
  /** Missing: the documentation's current version. */
  versionId?: string;
}

/** Why a map needs a warning; the backend's `LevelMapWarning`, with its `withdrawn` split by what was withdrawn, and one client case. */
export enum LevelMapWarning {
  /** The map documentation has no current version, so there is nothing to show. */
  noCurrentVersion = 'noCurrentVersion',
  /** The current version cannot be drawn as a map (not an image, not a converted PDF, not uploaded). */
  notSuitable = 'notSuitable',
  /** The version drawn is withdrawn; the map is still shown. */
  withdrawn = 'withdrawn',
  /** The map documentation is withdrawn; the map is still shown. */
  documentationWithdrawn = 'documentationWithdrawn',
  /** The documentation or its current version is not stored on the device (yet): nothing to show. */
  unavailable = 'unavailable',
}

export function isNoCurrentVersionWarning(warning: LevelMapWarning | undefined): boolean {
  return warning === LevelMapWarning.noCurrentVersion;
}

/** The map drawn is withdrawn, its version or its whole documentation: it is shown, but nothing is placed on it. */
export function isWithdrawalWarning(warning: LevelMapWarning | undefined): boolean {
  return warning === LevelMapWarning.withdrawn || warning === LevelMapWarning.documentationWithdrawn;
}

/** What a level shows as its map: the documentation and version drawn (if any) and the warning that goes with it. */
export interface LevelMapResolution<TVersion extends MapVersion = MapVersion> {
  readonly shown: { documentation: MapDocumentation<TVersion>; version: TVersion } | undefined;
  readonly warning: LevelMapWarning | undefined;
  /** The version that cannot be drawn, with a `notSuitable` warning: the warning tells why from its file. */
  readonly unsuitableVersion?: TVersion;
}

/** A level without a map: nothing drawn, nothing to warn about. */
export const NO_LEVEL_MAP: LevelMapResolution<never> = { shown: undefined, warning: undefined };

/**
 * Whether a version can be drawn as a level map: an uploaded image (png,
 * jpg, jpeg, webp) or an uploaded PDF whose image conversion succeeded. The
 * backend's `IsVersionSuitableAsMapUseCase`; every map consumer uses it.
 */
export function isVersionSuitableAsMap(version: MapVersion): boolean {
  return isUploadStatusSuccess(version.uploadStatus) && isSupportedAsMap(version.extension, version.imageConvertStatus);
}

/**
 * Resolves a map into what is drawn and what is warned about, as the
 * backend's `FindDocumentationAndVersionOnLevelUseCase` does for a level: the
 * version is drawn if it can be; without a current version, or with one that
 * cannot be drawn, nothing is drawn and the warning says why; a withdrawn
 * version or documentation is still drawn, with a warning. A map pinned to a
 * version (an issue's) draws that version by the same rules. Where the
 * backend throws on a reference it cannot find, the device may simply not
 * have the data yet: it warns `unavailable`.
 */
export function resolveMap<TVersion extends MapVersion>(
  map: MapReference | null | undefined,
  findDocumentation: (documentationId: string) => MapDocumentation<TVersion> | undefined
): LevelMapResolution<TVersion> {
  if (!map) {
    return NO_LEVEL_MAP;
  }
  const documentation = findDocumentation(map.documentationId);
  if (!documentation) {
    return { shown: undefined, warning: LevelMapWarning.unavailable };
  }
  if (map.versionId) {
    return resolveVersionShown(documentation, findById(documentation.versions, map.versionId));
  }
  if (!documentation.currentVersionId) {
    return { shown: undefined, warning: LevelMapWarning.noCurrentVersion };
  }
  return resolveVersionShown(documentation, findCurrentVersion(documentation));
}

function resolveVersionShown<TVersion extends MapVersion>(
  documentation: MapDocumentation<TVersion>,
  version: TVersion | undefined
): LevelMapResolution<TVersion> {
  if (!version) {
    return { shown: undefined, warning: LevelMapWarning.unavailable };
  }
  if (!isVersionSuitableAsMap(version)) {
    return { shown: undefined, warning: LevelMapWarning.notSuitable, unsuitableVersion: version };
  }
  return { shown: { documentation, version }, warning: findWithdrawalWarning(documentation, version) };
}

/** Which withdrawal the user is told about: the documentation's, as it takes every version with it, else the version's. */
function findWithdrawalWarning(documentation: Deletable, version: Deletable): LevelMapWarning | undefined {
  if (documentation.deleted) {
    return LevelMapWarning.documentationWithdrawn;
  }
  return version.deleted ? LevelMapWarning.withdrawn : undefined;
}
